import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Snackbar,
  CircularProgress,
  Alert,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  Autocomplete
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';

import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';
import { useTranslation } from 'react-i18next';
import AddSchoolDialog from './addSchoolDialog';
import ConfirmDeleteDialog from 'src/components/dialogs/delete/ConfirmDeleteDialog';
import { Edit, Delete } from '@mui/icons-material';
import { useLocaleText } from 'src/helper/localeText';
import { latinToCyrillic } from 'src/helper/helperFunction';

/* ADMIN STRANA ZA SKOLE */
const AdminSchools = () => {
  const [schools, setSchools] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolToDelete, setSchoolToDelete] = useState(null);
  const { t } = useTranslation();
  const localeText = useLocaleText();
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchSchools();
    fetchLocations();
  }, []);

  const fetchSchools = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/schools.php`);
      setSchools(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/locations.php`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleDialogToggle = (open, school) => {
    if (school) {
      setSelectedSchool(school);
    } else {
      setSelectedSchool(null);
    }
    setOpenDialog(open);
  };

  const handleConfirmDeleteToggle = (open, school) => {
    setOpenConfirmDeleteDialog(open);
    if (open) setSchoolToDelete(school);
  };

  const handleDeleteSchool = async () => {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/schools.php?id=${schoolToDelete.id}`
      );
      setSnackbarMessage(t('alerts.schoolDeleted'));
      fetchSchools();
    } catch (error) {
      console.error('Error deleting school:', error);
    } finally {
      setOpenConfirmDeleteDialog(false);
      setSnackbarOpen(true);
    }
  };

  const handleAddSchoolSuccess = () => {
    fetchSchools();
    setSnackbarMessage(t('alerts.schoolSaved'));
    setSnackbarOpen(true);
  };

  const filteredSchools = useMemo(() => {
    return schools.filter((school) => {
      const searchLowerCase = searchQuery.toLowerCase();
      const searchCyrillic = latinToCyrillic(searchLowerCase);
      const cityMatch = selectedCity
        ? school.zipcode === selectedCity.zipcode
        : true;

      return (
        cityMatch &&
        (school.name.toLowerCase().includes(searchLowerCase) ||
          school.name.toLowerCase().includes(searchCyrillic) ||
          school.street.toLowerCase().includes(searchLowerCase) ||
          school.street.toLowerCase().includes(searchCyrillic) ||
          school.telephone.includes(searchLowerCase) ||
          school.email.toLowerCase().includes(searchLowerCase) ||
          school.email.toLowerCase().includes(searchCyrillic))
      );
    });
  }, [schools, searchQuery, selectedCity]);

  const columns = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 90 },
      {
        field: 'name',
        headerName: t('columns.name'),
        width: 200,
        renderCell: (params) => (
          <span
            style={{ cursor: 'pointer', color: '#1c75bc' }}
            onClick={() => handleDialogToggle(true, params.row)}
          >
            {params.value}
          </span>
        )
      },
      {
        field: 'location',
        headerName: t('columns.location'),
        width: 200,
        valueGetter: (params) => {
          const location = locations.find(
            (loc) => loc.zipcode === params.row.zipcode
          );
          return location ? location.name : params.row.zipcode;
        }
      },
      { field: 'street', headerName: t('columns.street'), width: 200 },
      { field: 'telephone', headerName: t('columns.telephone'), width: 150 },
      { field: 'email', headerName: t('columns.email'), width: 200 },
      { field: 'professor', headerName: t('columns.professor'), width: 200 },
      {
        field: 'actions',
        type: 'actions',
        headerName: t('columns.actions'),
        width: 150,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<Edit sx={{ color: '#1c75bc', fontSize: '24px' }} />}
            label="Edit"
            onClick={() => handleDialogToggle(true, params.row)}
          />,
          <GridActionsCellItem
            icon={<Delete sx={{ color: '#e91e63', fontSize: '24px' }} />}
            label="Delete"
            onClick={() => handleConfirmDeleteToggle(true, params.row)}
          />
        ]
      }
    ],
    [locations, t]
  );
  // Filter options for Autocomplete (Cities)
  const filterCityOptions = (options, { inputValue }) => {
    const inputCyrillic = latinToCyrillic(inputValue);
    return options.filter((option) =>
      option.name.toLowerCase().includes(inputCyrillic.toLowerCase())
    );
  };
  return (
    <Box
      sx={{
        padding: 3,
        backgroundColor: '#f5f5f5',
        borderRadius: '15px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        maxWidth: '1800px' // Širi boks
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ color: '#2596be' }}>
        {t('adminschool.title')}
      </Typography>
      <Box sx={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <TextField
          variant="outlined"
          placeholder={t('search.placeholder')}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{
            width: '100%',
            backgroundColor: '#f5f7fa',
            borderRadius: '8px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px'
            }
          }}
        />

        <Autocomplete
          options={locations}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) =>
            option.zipcode === value.zipcode
          }
          filterOptions={filterCityOptions}
          onChange={(event, newValue) => {
            setSelectedCity(newValue);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.zipcode}>
              {option.name}
            </li>
          )}
          noOptionsText={t('pagination.noRowsLabel')}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('filter.city')}
              variant="outlined"
              sx={{
                width: '200px',
                backgroundColor: '#f5f7fa',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            />
          )}
        />
      </Box>
      <Button
        variant="contained"
        color="primary"
        onClick={() => handleDialogToggle(true)}
        sx={{
          marginBottom: 2,
          backgroundColor: '#2596be',
          borderRadius: '20px',
          '&:hover': {
            backgroundColor: '#1976d2'
          }
        }}
      >
        {t('adminschool.addNew')}
      </Button>
      <Paper elevation={3} sx={{ padding: '20px', borderRadius: '12px' }}>
        {loading ? (
          <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
        ) : (
          <DataGrid
            rows={filteredSchools}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[10, 25, 50]}
            disableColumnMenu
            autoHeight
            localeText={localeText}
            pagination
            componentsProps={{
              pagination: {
                showFirstButton: true,
                showLastButton: true,
                labelRowsPerPage: t('pagination.rowsPerPage'),
                labelDisplayedRows: ({ from, to, count }) =>
                  `${from}-${to} ${t('pagination.of')} ${
                    count !== -1 ? count : `${t('pagination.moreThan')} ${to}`
                  }`
              }
            }}
            sx={{
              '& .MuiDataGrid-root': {
                border: 'none',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '10px',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#2596be',
                color: '#ffffff',
                borderBottom: 'none',
                borderRadius: '12px 12px 0 0',
                fontSize: '16px'
              },
              '& .MuiDataGrid-cell': {
                borderBottom: 'none',
                padding: '12px',
                fontSize: '14px',
                color: '#333'
              },
              '& .MuiDataGrid-footerContainer': {
                backgroundColor: '#1c75bc',
                color: '#ffffff',
                borderTop: 'none',
                borderRadius: '0 0 12px 12px'
              }
            }}
          />
        )}
      </Paper>
      <AddSchoolDialog
        open={openDialog}
        handleClose={() => handleDialogToggle(false)}
        schoolData={selectedSchool}
        onSuccess={handleAddSchoolSuccess}
        locations={locations}
      />
      <ConfirmDeleteDialog
        open={openConfirmDeleteDialog}
        onClose={() => setOpenConfirmDeleteDialog(false)}
        onConfirm={handleDeleteSchool}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        sx={{
          '& .MuiAlert-root': {
            borderRadius: '8px',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminSchools;
